/**
 * V-NAV-ACCOUNT — ONE account dropdown, rendered as the rightmost control on every authed surface.
 *
 * ⛔⛔ THE DEFECT. The account menu was FRAGMENTED and, on the surface where it mattered most,
 * ABSENT:
 *   - storeApp's header had the full menu (My Account / The Vault / Supply Lab / Rewards /
 *     My Orders / Sign Out) but was not the rightmost control -- Admin Console and the cart
 *     sat after it;
 *   - MaestroNav (the homepage and Supply Lab shell) had a SPARSE menu: Vault and Sign Out only;
 *   - the desktop vault is a `z-50 fixed inset-0` OVERLAY, so it COVERED storeApp's header
 *     entirely -- inside the vault there was no account menu and no way to sign out at all;
 *   - the mobile vault screens are their own routes with their own headers, and had none either.
 * Four surfaces, three different answers, and one with nothing.
 *
 * ⭐ THE ITEM SET AND THE HANDLERS ARE THE CALLER'S. This component owns the PRESENTATION and the
 * open/close behaviour; every destination is a prop, so the surfaces cannot drift apart on what
 * "My Orders" does while still looking identical. A surface that cannot service an item (the
 * vault overlay has no gamification modal of its own, say) passes nothing for it and the item is
 * omitted rather than rendered dead -- a menu item that does nothing is worse than an absent one.
 *
 * ⚠ IT MUST OUTRANK THE VAULT OVERLAY. The overlay is z-50; this menu's panel is z-[70] and, when
 * a surface renders it as fixed chrome, the wrapper is above the overlay too. A dropdown that
 * opens UNDER the thing it is anchored over is invisible, which is the bug it is here to fix.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, CreditCard, LogOut, Package, Truck, User as UserIcon, Boxes } from 'lucide-react';

export default function AccountMenu({
  user,
  onAccount,
  onVault,
  onSupplyLab,
  onRewards,
  onOrders,
  onSignOut,
  testId = 'account-menu',
  triggerTestId,
  avatar = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Escape closes; a click outside closes. Both are registered only while open so the app is not
  // carrying two document-level listeners per surface for a menu nobody has touched.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClickOutside);
    };
  }, [isOpen]);

  if (!user) return null;

  const displayName = (user.name || user.email || '').trim();
  const initial = (displayName || '?').charAt(0).toUpperCase();
  const firstName = displayName.split(' ')[0];

  // ⚠ THE ARIA LABELS ARE NOT THE VISIBLE LABELS, DELIBERATELY. "Open your Vault" and "Sign out
  // of your account" say what the control DOES; "Vault" and "Sign Out" are what fits in a 48px
  // row. Screen-reader users get the sentence, sighted users get the word, and the existing nav
  // tests already assert the accessible names -- they are a contract, not decoration.
  const items = [
    { key: 'account', label: 'My Account', ariaLabel: 'My Account', Icon: UserIcon, onSelect: onAccount },
    { key: 'vault', label: 'The Vault', ariaLabel: 'Open your Vault', Icon: Package, onSelect: onVault },
    { key: 'supply-lab', label: 'Supply Lab', ariaLabel: 'Supply Lab', Icon: Boxes, onSelect: onSupplyLab },
    { key: 'rewards', label: 'Rewards', ariaLabel: 'Rewards', Icon: CreditCard, onSelect: onRewards },
    { key: 'orders', label: 'My Orders', ariaLabel: 'My Orders', Icon: Truck, onSelect: onOrders },
    { key: 'sign-out', label: 'Sign Out', ariaLabel: 'Sign out of your account', Icon: LogOut, onSelect: onSignOut },
  ].filter((item) => typeof item.onSelect === 'function');

  return (
    <div className="relative flex-shrink-0" ref={containerRef} data-testid={`${testId}-root`}>
      <button
        type="button"
        aria-label="Account menu"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={testId}
        data-testid={triggerTestId || `${testId}-trigger`}
        onClick={() => setIsOpen((open) => !open)}
        className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-900 border border-zinc-800 rounded-lg sm:rounded-xl hover:border-orange-500 transition-colors min-h-touch"
      >
        {/* ⚠ PLUGGABLE ON PURPOSE. MaestroNav's avatar is IDENTITY-aware (UserBadge via
            IdentityContext); rendering the plain initial for every surface would silently drop
            that. A surface with nothing special to show passes nothing and gets the initial. */}
        {avatar || (
          <>
            <div className="w-6 h-6 sm:w-7 sm:h-7 bg-orange-500 rounded-full flex items-center justify-center">
              <span className="text-[10px] sm:text-xs font-bold text-white">{initial}</span>
            </div>
            <span className="text-sm text-zinc-300 hidden sm:block">{firstName}</span>
          </>
        )}
        <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 hidden sm:block" />
      </button>

      <div
        id={testId}
        data-testid={testId}
        onClick={() => setIsOpen(false)}
        className={`absolute right-0 mt-2 w-48 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl transition-all z-[70] ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="p-3 border-b border-zinc-800">
          <p className="text-sm font-semibold text-white flex items-center gap-2">
            {displayName}
            {user.is_admin && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500/20 text-red-400 rounded">ADMIN</span>
            )}
          </p>
          <p className="text-xs text-zinc-500">{user.email}</p>
        </div>
        {items.map((item) => {
          const { key, label, Icon, onSelect } = item;
          return (
          <button
            key={key}
            type="button"
            data-testid={`${testId}-${key}`}
            aria-label={item.ariaLabel}
            onClick={onSelect}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors min-h-touch"
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
          );
        })}
      </div>
    </div>
  );
}
